import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import "react-toastify/dist/ReactToastify.css";
import MainLayout from "../../layouts/MainLayout";
import {
  useFetchAuthorisation,
  useFetchUserData
} from "../../utils/AppFunctions";
import config from "../../app-config";
import { get_mobile_os } from "../../utils/helper";

export default function AppRedirectPage() {
  const { url } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(5);
  const [downloadLinks, setDownloadLinks] = useState([]);

  const [defaultLink, setDefaultLink] = useState("")

  useEffect(() => {
    if (!url) {
      setError("No URL provided for redirection");
      return;
    }

    // Start countdown and redirect
    const bmcUrl = `bmc://${url}`;

    console.log("Redirecting to:", bmcUrl);

    // Start countdown
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          // Attempt to redirect to app
          window.location.href = bmcUrl;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [url]);

  const handleManualRedirect = () => {
    if (url) {
      const bmcUrl = `bmc://${url}`;
      window.location.href = bmcUrl;
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };



  useEffect(() => {
    const fetchDownloadLinks = async () => {
      try {
        const res = await fetch(`${config.endpoint}/config`);
        const data = await res.json();
        const links = data.data.general;
        console.log(links);
        setDownloadLinks(links);
      } catch (err) {
        return err;
      }
    };

    fetchDownloadLinks();
  }, []);

  // Auto redirection based on OS
  useEffect(() => {
    if (!downloadLinks.ios_link) return;
    if (!downloadLinks.android_link) return;
    const os = get_mobile_os();
    console.log(`OS: ${os} detected`)
    switch (os) {
      case "macos":
        setDefaultLink(downloadLinks.ios_link);
        break;
      case "android":
        setDefaultLink(downloadLinks.android_link);
        break;
      case "ios":
        setDefaultLink(downloadLinks.ios_link);
        break;
      case "windows":
        break;
      default:
        break;
    }
  }, []);



  return (
    <MainLayout navSelected={"Home"}>
      <div className="flex w-full items-center justify-center min-h-[calc(100vh-64px)] bg-gray-50 px-4 py-8">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center mx-auto">
          {error ? (
            // Error State
            <>
              <div className="mb-6">
                <div className="w-20 h-20 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Redirect Error</h1>
                <p className="text-gray-600 mb-6">{error}</p>
              </div>

              <button
                onClick={handleGoBack}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200"
              >
                Go Back
              </button>
            </>
          ) : (
            // Redirect State
            <>
              <div className="mb-6">
                <div className=" rounded-full flex items-center justify-center">
                  <img src={"/app/logo_teal.png"} alt="" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Opening BMC plus</h1>
                <p className="text-gray-600 mb-4">
                  Redirecting you to the BMC mobile app...
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  Destination: <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">bmc://{url}</code>
                </p>
              </div>

              {/* Countdown Circle */}
              <div className="mb-6">
                <div className="relative w-16 h-16 mx-auto">
                  <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 64 64">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="4"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="4"
                      strokeDasharray={`${2 * Math.PI * 28}`}
                      strokeDashoffset={`${2 * Math.PI * 28 * (countdown / 5)}`}
                      className="transition-all duration-1000 ease-linear"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-bold text-blue-600">{countdown}</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Auto-redirecting in {countdown} seconds
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleManualRedirect}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200"
                >
                  Open App Now
                </button>

                <button
                  onClick={handleGoBack}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-3 px-4 rounded-lg transition duration-200"
                >
                  Cancel
                </button>
              </div>

              {(defaultLink || downloadLinks?.ios_link || downloadLinks?.android_link) && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Don't have the BMC plus?
                    <button
                      onClick={() => window.open(defaultLink || downloadLinks.ios_link || downloadLinks.android_link, '_blank')}
                      className="text-blue-600 hover:text-blue-700 ml-1 underline bg-transparent border-none cursor-pointer"
                    >
                      Download it here
                    </button>
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </MainLayout>
  )
}